import { __awaiter } from "tslib";
import { AuthError, ClientAuthError, ClientAuthErrorCodes, InteractionRequiredAuthError, } from '@azure/msal-browser';
import { NestedAppAuthErrorResponseStatus, NestedAppAuthMethodType, } from '@office-start/auth-types';
import { fetchOfficeComponentContext } from '@office-start/component-context-service';
import { fetchFeatureSettings, getFeatureSettings, } from '@office-start/feature-settings-service';
import { getNestedAppToken, GetTokenType, } from '@office-start/hybrid-auth-service';
import { OfficeWorkload } from '@office-start/officehome-types';
import { logError, logFeature, logPerformanceWithParams, } from '@office-start/telemetry-service';
import JwtDecode from 'jwt-decode';
import { getWamAccessToken, isWindowsAuthAvailable } from './WindowsTokens';
export class NestedAppAuthService {
    constructor(user) {
        this.user = user;
    }
    isDeeplyNestedAuthSupported() {
        return false;
    }
    execute(payload, origin, isTrustedOrigin) {
        try {
            const startTime = Date.now();
            // TODO: origin is a string at runtime, but it should be a URL
            // hack - conevert origin to URL explicitly
            origin = new URL(origin.toString());
            const payloadObject = JSON.parse(payload);
            return this.handleRequest(payloadObject, origin).then((response) => {
                var _a;
                // log performance metrics
                const endTime = Date.now();
                const duration = endTime - startTime;
                const reqMethod = payloadObject.method;
                logPerformanceWithParams({
                    eventName: `${"Nested_App_Auth_Execute" /* KnownPerfInstrumentationEvents.NestedAppAuthExecute */}_${reqMethod}`,
                    latency: duration,
                    instrumentationParams: {
                        requestMethod: reqMethod,
                        success: response.success,
                        duration,
                        requestId: payloadObject.requestId,
                    },
                });
                logFeature({
                    featureName: "NestedAppAuth" /* KnownFeatureNames.NestedAppAuth */,
                    eventName: reqMethod,
                    params: {
                        success: response.success,
                        duration,
                        requestId: payloadObject.requestId,
                        clientId: 'tokenParams' in payloadObject
                            ? payloadObject.tokenParams.clientId
                            : '',
                        status: response.success
                            ? NestedAppAuthErrorResponseStatus.SUCCESS
                            : (_a = response.error) === null || _a === void 0 ? void 0 : _a.status,
                    },
                });
                // serialize and return the response
                return JSON.stringify(response);
            });
        }
        catch (error) {
            return Promise.resolve(JSON.stringify(generateErrorResponse(undefined, NestedAppAuthErrorResponseStatus.TRANSIENT_ERROR, 'execute', `failed with exception: ${error}`)));
        }
    }
    handleRequest(request, origin) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestMethod = request.method;
            switch (requestMethod) {
                case NestedAppAuthMethodType.GET_INIT_CONTEXT:
                    const initContext = yield this.getInitContext(request);
                    return initContext;
                case NestedAppAuthMethodType.GET_TOKEN:
                case NestedAppAuthMethodType.GET_TOKEN_POPUP:
                    const tokenResponse = yield this.getTokenResponse(request, origin, requestMethod === NestedAppAuthMethodType.GET_TOKEN_POPUP);
                    return tokenResponse;
                default:
                    return generateErrorResponse(request, NestedAppAuthErrorResponseStatus.PERSISTENT_ERROR, 'handleRequest', `method not supported: ${requestMethod}`);
            }
        });
    }
    getTokenResponse(request, origin, allowUserInteraction) {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const officeComponentContext = yield fetchOfficeComponentContext();
                const nestedAppRedirectUri = getNestedAppRedirectUri(origin);
                if (isWindowsAuthAvailable &&
                    officeComponentContext.workload == OfficeWorkload.Hwa) {
                    const result = yield getWamAccessToken('', // resource URL is not used in NAA flow
                    this.user, allowUserInteraction, request.tokenParams.scope, {
                        clientId: request.tokenParams.clientId,
                        redirectUri: nestedAppRedirectUri !== null && nestedAppRedirectUri !== void 0 ? nestedAppRedirectUri : '',
                    });
                    // error handling
                    if (!result.responseData ||
                        !result.responseData.length ||
                        !result.responseData[0].token ||
                        !result.responseData[0].id_token ||
                        result.responseStatus !==
                            Windows.Security.Authentication.Web.Core
                                .WebTokenRequestStatus.success) {
                        const message = `unable to acquire token from WebTokenRequestResult. responseStatus: ${result.responseStatus}  isTokenPresent: ${!!((_a = result.responseData) === null || _a === void 0 ? void 0 : _a[0].token)}  isIdTokenPresent: ${!!((_b = result.responseData) === null || _b === void 0 ? void 0 : _b[0].id_token)}`;
                        return generateErrorResponse(request, getNestedAppErrorStatusFromWAMError(result.responseStatus), 'getTokenResponse', message);
                    }
                    return this.validateAndTransformTokenResponse(request, result.responseData[0].token, result.responseData[0].id_token, getHostAndPortFromUrl(new URL(result.responseData[0].webAccount.properties.lookup('Authority'))));
                }
                else {
                    const featureSettingsResult = getFeatureSettings();
                    const settings = (featureSettingsResult === null || featureSettingsResult === void 0 ? void 0 : featureSettingsResult.result)
                        ? featureSettingsResult
                        : yield fetchFeatureSettings();
                    const authOptions = (_c = settings === null || settings === void 0 ? void 0 : settings.result) === null || _c === void 0 ? void 0 : _c.authOptions;
                    if (!!nestedAppRedirectUri &&
                        !!request.tokenParams.clientId &&
                        !!(authOptions === null || authOptions === void 0 ? void 0 : authOptions.aadAppId) &&
                        !!(authOptions === null || authOptions === void 0 ? void 0 : authOptions.aadAuthorityUrlRoot) &&
                        !!(authOptions === null || authOptions === void 0 ? void 0 : authOptions.spaRedirectUrlPath)) {
                        const result = yield getNestedAppToken({
                            type: GetTokenType.NESTED_APP,
                            tokenConsumer: 'MetaOS',
                            forceFetch: false,
                            resourceId: '',
                            scope: request.tokenParams.scope,
                            isBoot: undefined,
                            claims: undefined,
                            silent: !allowUserInteraction,
                            capDialogData: {
                                category: 'MetaOSApp',
                            },
                            clientId: authOptions.aadAppId,
                            redirectUri: `https://${window.location.host}${authOptions.spaRedirectUrlPath}`,
                            nestedClientRedirectUri: nestedAppRedirectUri,
                            nestedClientId: request.tokenParams.clientId,
                            requestId: request.requestId,
                        });
                        // error handling
                        if (!result.success || !result.idToken || !result.token) {
                            const message = `unable to acquire token from NestedAppTokenResponse. responseStatus: ${result.success}  isTokenPresent: ${!!result.token}  isIdTokenPresent: ${!!result.idToken} msalErrorCode: ${result.error} requestId: ${request.requestId}`;
                            return generateErrorResponse(request, getNestedAppErrorStatusFromMSALError(result.error), 'getTokenResponse', message);
                        }
                        return this.validateAndTransformTokenResponse(request, result.token, result.idToken, (_e = (_d = result.accountInfo) === null || _d === void 0 ? void 0 : _d.environment) !== null && _e !== void 0 ? _e : (yield getEnvironmentFromAuthOptions()), result.expiryMs);
                    }
                    else {
                        return generateErrorResponse(request, NestedAppAuthErrorResponseStatus.TRANSIENT_ERROR, 'getTokenResponse', 'Invalid request');
                    }
                }
            }
            catch (error) {
                return generateErrorResponse(request, NestedAppAuthErrorResponseStatus.TRANSIENT_ERROR, 'getTokenResponse', `getTokenResponse failed with exception: ${error}`);
            }
        });
    }
    validateAndTransformTokenResponse(request, accessToken, idToken, environment, expiryMs) {
        const tokenPayload = JwtDecode(idToken);
        if (!tokenPayload) {
            return generateErrorResponse(request, NestedAppAuthErrorResponseStatus.TRANSIENT_ERROR, 'validateAndTransformTokenResponse', `Empty token payload was received for requestId: ${request.requestId}`);
        }
        else if (tokenPayload.aud !== request.tokenParams.clientId) {
            return generateErrorResponse(request, NestedAppAuthErrorResponseStatus.TRANSIENT_ERROR, 'validateAndTransformTokenResponse', `Invalid audience received in token-payload for ClientId: ${request.tokenParams.clientId}. TokenAudience: ${tokenPayload.aud} RequestId: ${request.requestId}`);
        }
        return {
            messageType: 'NestedAppAuthResponse',
            requestId: request.requestId,
            success: true,
            token: {
                access_token: accessToken,
                id_token: idToken,
                expires_in: expiryMs ? (expiryMs - Date.now()) / 1000 : 0,
            },
            account: {
                localAccountId: this.user.profile.oid,
                username: this.user.profile.upn,
                tenantId: this.user.profile.tid,
                homeAccountId: getHomeAccountId(this.user),
                name: this.user.profile.displayName,
                environment: environment,
            },
        };
    }
    getInitContext(request) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const response = {
                    messageType: 'NestedAppAuthResponse',
                    requestId: request.requestId,
                    success: true,
                    initContext: {
                        sdkName: 'Harmony PWA',
                        sdkVersion: 'V1',
                        capabilities: {
                            queryAccount: false,
                        },
                        accountContext: {
                            homeAccountId: getHomeAccountId(this.user),
                            environment: yield getEnvironmentFromAuthOptions(),
                            tenantId: this.user.profile.tid,
                        },
                    },
                };
                return response;
            }
            catch (error) {
                return generateErrorResponse(request, NestedAppAuthErrorResponseStatus.TRANSIENT_ERROR, 'getInitContext', `getInitContext failed with exception: ${error}`);
            }
        });
    }
}
function getHomeAccountId(user) {
    return user.profile.tid && user.profile.oid
        ? `${user.profile.oid}.${user.profile.tid}`
        : '';
}
function getEnvironmentFromAuthOptions() {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        const featureSettingsResult = getFeatureSettings();
        const settings = (featureSettingsResult === null || featureSettingsResult === void 0 ? void 0 : featureSettingsResult.result)
            ? featureSettingsResult
            : yield fetchFeatureSettings();
        const authOptions = (_a = settings === null || settings === void 0 ? void 0 : settings.result) === null || _a === void 0 ? void 0 : _a.authOptions;
        const aadAuthorityUrlRoot = authOptions === null || authOptions === void 0 ? void 0 : authOptions.aadAuthorityUrlRoot;
        if (aadAuthorityUrlRoot) {
            return getHostAndPortFromUrl(new URL(aadAuthorityUrlRoot));
        }
        else {
            throw new Error('AAD authority URL root is not available');
        }
    });
}
function generateErrorResponse(request, status, methodName, description) {
    var _a;
    logError({
        eventName: "NestedAppAuth" /* KnownFeatureNames.NestedAppAuth */,
        errorType: 'Nested_App_Auth_Error',
        message: description,
        debugInfo: `Error for: ${methodName} with status-code: ${status}`,
    });
    return {
        messageType: 'NestedAppAuthResponse',
        requestId: (_a = request === null || request === void 0 ? void 0 : request.requestId) !== null && _a !== void 0 ? _a : '',
        success: false,
        error: {
            status,
            description,
        },
    };
}
function getNestedAppRedirectUri(redirectUrl) {
    if (!redirectUrl) {
        return undefined;
    }
    const nestedAppRedirectUri = `brk-multihub://${getHostAndPortFromUrl(redirectUrl)}`;
    return nestedAppRedirectUri;
}
function getHostAndPortFromUrl(url) {
    const hostPart = url.hostname;
    const portPart = url.port ? `:${url.port}` : '';
    return `${hostPart}${portPart}`;
}
function getNestedAppErrorStatusFromWAMError(status) {
    switch (status) {
        case Windows.Security.Authentication.Web.Core.WebTokenRequestStatus
            .userCancel:
            return NestedAppAuthErrorResponseStatus.USER_CANCEL;
        case Windows.Security.Authentication.Web.Core.WebTokenRequestStatus
            .userInteractionRequired:
            return NestedAppAuthErrorResponseStatus.USER_INTERACTION_REQUIRED;
        case Windows.Security.Authentication.Web.Core.WebTokenRequestStatus
            .accountSwitch:
        case Windows.Security.Authentication.Web.Core.WebTokenRequestStatus
            .accountProviderNotAvailable:
        case Windows.Security.Authentication.Web.Core.WebTokenRequestStatus
            .providerError:
            return NestedAppAuthErrorResponseStatus.TRANSIENT_ERROR;
        default:
            return NestedAppAuthErrorResponseStatus.PERSISTENT_ERROR;
    }
}
export function getNestedAppErrorStatusFromMSALError(error) {
    if (error instanceof AuthError) {
        if (error instanceof InteractionRequiredAuthError) {
            return NestedAppAuthErrorResponseStatus.USER_INTERACTION_REQUIRED;
        }
        else if (error instanceof ClientAuthError) {
            switch (error.errorCode) {
                case ClientAuthErrorCodes.userCanceled:
                    return NestedAppAuthErrorResponseStatus.USER_CANCEL;
                case ClientAuthErrorCodes.noNetworkConnectivity:
                    return NestedAppAuthErrorResponseStatus.NO_NETWORK;
                case ClientAuthErrorCodes.noAccountFound:
                    return NestedAppAuthErrorResponseStatus.ACCOUNT_UNAVAILABLE;
                case ClientAuthErrorCodes.nestedAppAuthBridgeDisabled:
                    return NestedAppAuthErrorResponseStatus.DISABLED;
                default:
                    return NestedAppAuthErrorResponseStatus.PERSISTENT_ERROR;
            }
        }
    }
    return NestedAppAuthErrorResponseStatus.PERSISTENT_ERROR;
}
//# sourceMappingURL=NestedAppAuthenticationService.js.map